import { getProductTypeOptionsMap } from 'config/loan.constants'
import { uwSummaryDefaultInputs } from 'pages/Loan/Underwriting/UnderwritingSummary/constants'
import { getAdminConfig } from 'services'

import { contractorInputPropertyFields } from './ContractorFieldProperties/constants'
import { defaultCounterPartyConfigInputs } from './CounterParty/constant'
import { productTypeSelectInputs, ruleInputs as creditReportRuleInputs } from './CreditReportDefaults/constants'
import { defaultInputs as documentGenerateInputs, limitCategories } from './DocumentGenerate/constants'
import { defaultInputs as emailSetupInputs } from './EmailSetup/constants'
import { featureInputs, subMenus as featureSubMenus } from './Features/constant'
import { defaultInputs as feeInputs } from './Fee/constants'
import { defaultFooterConfigInputs } from './Footer/constants'
import { defaultInputs as formDefaultsInputs } from './FormDefaults/constants'
import {
  defaultInputs as globalSettingsInputs,
  defaultInputsV2 as globalSettingsV2Inputs,
  uiInputs,
} from './GlobalSettings/constants'
import { settingInputs as hubspotSyncInputs } from './HubspotSync/constants'
import { closingCostSetupInputs } from './HUD1/ClosingCostSetup/constant'
import { getLinkConfigurationFields } from './Links/constants'
import { defaultInputs as loanFieldPropertyInputs } from './LoanFieldsProperties/VisiblePropertyModal/constants'
import { defaultInputs as loanPipelineHeaderInputs } from './LoanPipelineHeadersByAccountTypes/constants'
import { defaultInputs as loanStatusLabelInputs } from './LoanStatusLabel/constants'
import {
  defaultInputs as loanActionInputs,
  emailInputs as loanActionEmailInputs,
  statusChangeRuleInputs,
} from './LoanSubmission/constants'
import { menus } from './menus'
import { MldsDefaultInputs } from './MLDS/constant'
import {
  postageInputs as printMailPostageInputs,
  rateCardInputs as printMailRateCardInputs,
  settingInputs as printMailInputs,
} from './PrintMail/constants'
import { getTabConfigs } from './RulesConfiguration/constants'
import { defaultLockExtensionInputs, defaultProductInputs, defaultProgramInputs } from './SecondaryMarketing'
import { defaultInputs as businessHoursInputs } from './SecondaryMarketing/BusinessHours/constants'
import { defaultInputs as loanProgramInputs } from './SecondaryMarketing/LoanProgams/constants'
import { getBasicInputs, getFlatPenaltyInputs, getInterestBasedInputs } from './Servicing/PrepaymentPenalty/constant'
import { SERVICING_INFO_TABS } from './ServicingInformation/types'
import { defaultInputs as staticFileInputs } from './StaticFiles/constant'
import { getTextConfigurationFields } from './TextsConfiguration/constants'

/** One thing the Configuration search can jump to. */
export type ConfigSearchEntry = {
  id: string
  /** Top-level section (a value of `menus`) the entry lives in. */
  menu: string
  /** Sub-tab or field group inside that section, when known. */
  section?: string
  label: string
  keywords?: string
}

type FieldMap = Record<string, any>

const isObject = (v: any) => !!v && typeof v === 'object' && !Array.isArray(v)

/** `commitmentFee` -> `Commitment Fee`, for the handful of fields with no title. */
const humanize = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()

const push = (out: ConfigSearchEntry[], seen: Set<string>, entry: ConfigSearchEntry) => {
  const label = entry.label.trim()
  if (!label || label.length > 120) return
  const dk = `${entry.menu}|${(entry.section || '').toLowerCase()}|${label.toLowerCase()}`
  if (seen.has(dk)) return
  seen.add(dk)
  out.push({ ...entry, label })
}

/**
 * Walk one `RenderInput` field map into search entries.
 *
 * A `inputType: 'section'` entry is a heading in the rendered form, so it both
 * becomes an entry of its own and files every field after it underneath itself.
 */
const walkFields = (
  fields: FieldMap,
  menu: string,
  section: string | undefined,
  out: ConfigSearchEntry[],
  seen: Set<string>,
  depth = 0,
) => {
  if (!isObject(fields)) return
  let group = section
  Object.keys(fields).forEach((key) => {
    const field = fields[key]
    if (!isObject(field)) return

    // Nested maps (Fee, Emails) keep one map of inputs per tab.
    if (!field.inputType) {
      if (depth < 2) walkFields(field, menu, humanize(key), out, seen, depth + 1)
      return
    }

    const title = String(field.title || '').trim() || humanize(key)
    if (String(field.inputType).toLowerCase() === 'section') {
      group = title
      push(out, seen, { id: `${menu}-${key}`, menu, section, label: title, keywords: `section ${key}` })
      return
    }
    push(out, seen, {
      id: `${menu}-${group || ''}-${key}`,
      menu,
      section: group,
      label: title,
      keywords: `${key} ${String(field.tooltip || '')}`,
    })
  })
}

/** A field map to index, named by the section (and optional sub-tab) it renders in. */
type Source = { menu: string; section?: string; build: () => FieldMap }

const sources = (): Source[] => [
  { menu: menus.globalSettings, build: globalSettingsInputs },
  { menu: menus.globalSettings, build: globalSettingsV2Inputs },
  { menu: menus.globalSettings, section: 'Interface', build: uiInputs },
  { menu: menus.globalSettings, section: 'Product', build: defaultProductInputs },
  { menu: menus.globalSettings, section: 'Lock Extension', build: defaultLockExtensionInputs },
  { menu: menus.globalSettings, section: 'Program', build: defaultProgramInputs },
  { menu: menus.fieldProperties, section: 'Loan Field Properties', build: loanFieldPropertyInputs },
  { menu: menus.fieldProperties, section: 'Contractor Field Properties', build: contractorInputPropertyFields },
  { menu: menus.loanAction, build: loanActionInputs },
  { menu: menus.loanAction, section: 'Email', build: loanActionEmailInputs },
  { menu: menus.loanAction, section: 'Status Change Rule', build: () => statusChangeRuleInputs({}) },
  { menu: menus.texts, build: getTextConfigurationFields },
  { menu: menus.emailSetup, build: emailSetupInputs },
  { menu: menus.servicing, section: 'Prepayment Penalty', build: getBasicInputs },
  { menu: menus.servicing, section: 'Prepayment Penalty', build: getFlatPenaltyInputs },
  { menu: menus.servicing, section: 'Prepayment Penalty', build: getInterestBasedInputs },
  { menu: menus.links, build: getLinkConfigurationFields },
  { menu: menus.features, section: 'Quick Link', build: () => featureInputs({}) },
  { menu: menus.loanStatusLabel, build: () => loanStatusLabelInputs({}) },
  { menu: menus.hud1, section: 'Closing Costs', build: closingCostSetupInputs },
  { menu: menus.footer, build: defaultFooterConfigInputs },
  { menu: menus.underwriting, section: 'Underwriting Summary', build: uwSummaryDefaultInputs },
  { menu: menus.formDefaults, build: formDefaultsInputs },
  { menu: menus.fee, build: feeInputs },
  { menu: menus.mlds, build: MldsDefaultInputs },
  { menu: menus.secondaryMarketing, section: 'Business Hours', build: businessHoursInputs },
  { menu: menus.secondaryMarketing, section: 'Loan Programs', build: loanProgramInputs },
  { menu: menus.counterParty, build: defaultCounterPartyConfigInputs },
  { menu: menus.creditReportDefaults, build: () => productTypeSelectInputs(getProductTypeOptionsMap()) },
  { menu: menus.creditReportDefaults, section: 'Rule', build: creditReportRuleInputs },
  { menu: menus.loanPipelineHeaders, build: loanPipelineHeaderInputs },
  { menu: menus.staticFiles, build: staticFileInputs },
  { menu: menus.hud1, section: 'Document Generate', build: documentGenerateInputs },
  { menu: menus.hubspotSync, build: hubspotSyncInputs },
  { menu: menus.printMail, build: printMailInputs },
  { menu: menus.printMail, section: 'Default postage', build: printMailPostageInputs },
  { menu: menus.printMail, section: 'Rate card', build: printMailRateCardInputs },
]

/**
 * Sub-tabs and list labels that only exist inside a section's own JSX, so they
 * cannot be read off a field map. Jumping to one selects its sub-tab.
 */
const staticEntries: { menu: string; section?: string; label: string; keywords?: string }[] = [
  { menu: menus.globalSettings, label: 'Loan Structure' },
  { menu: menus.globalSettings, label: 'Views' },
  { menu: menus.globalSettings, label: 'Manual Pricing', keywords: 'rate price' },
  { menu: menus.globalSettings, label: 'Security', keywords: 'mfa password jwt token expire' },
  { menu: menus.globalSettings, label: 'Setup', keywords: 'lei mers esign' },
  { menu: menus.globalSettings, label: 'Colors', keywords: 'brand theme color logo' },
  { menu: menus.globalSettings, label: 'AI Configuration', keywords: 'assistant gemini openai engine model' },
  // Nexys connection / pull settings live under Vendors → Nexys (not Global Settings).

  { menu: menus.fieldProperties, section: 'Loan Field Properties', label: 'Loan Field Properties', keywords: 'visible required read only field' },
  {
    menu: menus.fieldProperties,
    section: 'Contractor Field Properties',
    label: 'Contractor Field Properties',
    keywords: 'contractor field visible required',
  },
  { menu: menus.loanStatusRestrictions, label: 'Loan Status Restrictions', keywords: 'restrict status account type' },
  { menu: menus.loanStatusRestrictions, label: 'Workflows', keywords: 'workflow condition generate document' },
  {
    menu: menus.formDefaults,
    section: 'Commitment Letters',
    label: 'Commitment Letters',
    keywords: 'commitment letter prepayment penalty interest only months documents',
  },

  { menu: menus.servicing, section: 'Tab', label: 'Calendar', keywords: 'holiday business day' },
  { menu: menus.servicing, section: 'Calendar', label: 'Holiday Name' },
  { menu: menus.servicing, section: 'Calendar', label: 'Recurring (every year)' },
  { menu: menus.servicing, section: 'Tab', label: 'Prepayment Penalty', keywords: 'stepdown penalty yield' },

  { menu: menus.features, section: 'Tab', label: 'Quick Links' },
  { menu: menus.features, section: 'Tab', label: 'Borrower Quick Links' },
  { menu: menus.features, section: 'Tab', label: 'Underwriting Floater Quick Links' },

  { menu: menus.bubbles, label: 'Origination Pipeline', keywords: 'bubble count' },
  { menu: menus.bubbles, label: 'Servicing Pipeline', keywords: 'bubble count' },
  { menu: menus.bubbles, label: 'Borrower Pipeline', keywords: 'bubble count' },

  { menu: menus.exceptionRequests, label: 'Exception Types' },
  { menu: menus.exceptionRequests, label: 'Exception Factors' },
  { menu: menus.exceptionRequests, label: 'Submission Message' },
  { menu: menus.exceptionRequests, label: 'Attachment is Required' },

  { menu: menus.loanStatusLabel, section: 'Tab', label: 'Origination' },
  { menu: menus.loanStatusLabel, section: 'Tab', label: 'Servicing' },

  { menu: menus.interface, label: 'Custom Logo Link' },
  { menu: menus.interface, label: 'Background' },
  { menu: menus.interface, label: 'Register Form', keywords: 'sign up account fields' },
  { menu: menus.interface, label: 'Input Component Type' },
  { menu: menus.interface, label: 'Select Options' },

  { menu: menus.paymentTerms, label: 'Payment Terms and Conditions', keywords: 'terms text stripe' },
  { menu: menus.paymentTerms, section: 'Tab', label: 'Servicing', keywords: 'servicing payment make a payment terms' },

  { menu: menus.hud1, label: 'Closing Cost Setup', keywords: 'gfe trid line hud fee' },

  { menu: menus.underwriting, section: 'Underwriting Summary', label: 'Underwriting Summary', keywords: 'uw summary visible fields' },
  { menu: menus.underwriting, section: 'Expiration Dates', label: 'Expiration Dates', keywords: 'expiration date type' },

  ...SERVICING_INFO_TABS.map((tab) => ({
    menu: menus.servicingInformation,
    section: 'Tab',
    label: tab,
  })),

  { menu: menus.hmda, section: 'Tab', label: 'Action Taken' },
  { menu: menus.hmda, section: 'Tab', label: 'Conversion' },
  { menu: menus.hmda, section: 'Tab', label: 'Default Properties' },
  {
    menu: menus.hmda,
    section: 'Tab',
    label: 'Filing Information',
    keywords: 'transmittal federal agency contact calendar year tax id lei',
  },

  { menu: menus.mortgageCallReport, section: 'Tab', label: 'Filer & States', keywords: 'nmls rmla licensed' },
  { menu: menus.mortgageCallReport, section: 'Filer & States', label: 'Company NMLS ID' },
  { menu: menus.mortgageCallReport, section: 'Filer & States', label: 'Legal Name (as NMLS holds it)' },
  { menu: menus.mortgageCallReport, section: 'Filer & States', label: 'Fiscal Year End (MM-DD)' },
  { menu: menus.mortgageCallReport, section: 'Filer & States', label: 'Licensed States' },
  { menu: menus.mortgageCallReport, section: 'Filer & States', label: 'Foreclosure Servicing Statuses' },
  { menu: menus.mortgageCallReport, section: 'Filer & States', label: 'Bankruptcy Servicing Statuses' },
  { menu: menus.mortgageCallReport, section: 'Tab', label: 'Application Dispositions' },
  { menu: menus.mortgageCallReport, section: 'Tab', label: 'Financial Condition Accounts' },
  { menu: menus.mortgageCallReport, section: 'Tab', label: 'XML Field Map' },

  { menu: menus.loanSubmissionHierarchy, label: 'Loan Submission Hierarchy', keywords: 'account types hierarchy' },
  {
    menu: menus.userSwitchVisibility,
    label: 'Switch User Visibility',
    keywords: 'impersonate switch user borrower profile hierarchy',
  },
  { menu: menus.orderValuation, label: 'Valuation Vendor', keywords: 'appraisal amc vendor order' },
  { menu: menus.hubspotSync, label: 'Lead Properties', keywords: 'hubspot crm mapping property lead' },
  { menu: menus.hubspotSync, label: 'Contact Properties', keywords: 'hubspot crm mapping property contact borrower' },
  { menu: menus.accountTypes, label: 'Manage Account Types', keywords: 'account type enable rename' },
  {
    menu: menus.budgetTemplates,
    label: 'Budget Templates',
    keywords: 'budget scope of work template line item section rehab construction default',
  },

  { menu: menus.floaterTabs, label: 'Visible Account Types' },
  { menu: menus.floaterTabs, label: 'Editable Account Types' },
  { menu: menus.floaterTabs, label: 'Loan Details Fields', keywords: 'floater field' },

  { menu: menus.secondaryMarketing, section: 'Tab', label: 'Loan Programs' },
  { menu: menus.secondaryMarketing, section: 'Tab', label: 'Business Hours' },
  { menu: menus.secondaryMarketing, section: 'Tab', label: 'Rules' },
  { menu: menus.secondaryMarketing, section: 'Tab', label: 'Screen Fields', keywords: 'field order secondary marketing lock investor pricing' },

  { menu: menus.tasks, section: 'Tab', label: 'Hardcoded Tasks' },
  { menu: menus.documents, section: 'Tab', label: 'Generated Signed PDF', keywords: 'condition pdf' },

  { menu: menus.collateralReview, label: 'Collateral Review', keywords: 'field input type select options' },
  { menu: menus.borrowerPipelineAttentionTitles, label: 'Title (used when conditions below match)' },
  { menu: menus.loanOverviewAccess, label: 'Access Conditions', keywords: 'overview access restrict' },
  {
    menu: menus.printMail,
    label: 'Per-communication postage',
    keywords: 'print mail purpose postage statement late notice tax form 1098 certified',
  },
]

/** Extra words that should match a section even when they are not on screen. */
const menuKeywords: Record<string, string> = {
  [menus.globalSettings]: 'company global limits min max defaults',
  [menus.fieldProperties]: 'visible required read only lock field origin contractor',
  [menus.loanAction]: 'submission action email status change',
  [menus.rules]: 'email rules task generation notification',
  [menus.servicing]: 'holiday calendar prepayment penalty',
  [menus.features]: 'menu quick link submenu page',
  [menus.hud1]: 'closing cost gfe trid settlement statement hud docgen template document limits',
  [menus.partyEligibility]: 'appraiser settlement agent contractor guarantor banker',
  [menus.fee]: 'credit report appraisal fee cost broker mortgage points',
  [menus.mlds]: 'mortgage loan disclosure statement',
  [menus.mortgageCallReport]: 'nmls mcr rmla filer quarterly',
  [menus.creditReportDefaults]: 'fico score selection method',
  [menus.hubspotSync]: 'hubspot crm lead contact sync mapping owner',
  [menus.staticFiles]: 'file upload static asset',
  [menus.floaterTabs]: 'floater tab loan details',
  [menus.counterParty]: 'counterparty workflow approval',
  [menus.emailSetup]: 'smtp sender domain from address',
  [menus.texts]: 'wording copy description message',
  [menus.interface]: 'logo background register form branding',
  [menus.printMail]: 'print mail post letter envelope postage lob usps certified rate card return address 1098',
}

let cached: ConfigSearchEntry[] | null = null

/** The static half of the index: every section, plus every field map it renders. */
export const getConfigSearchIndex = (): ConfigSearchEntry[] => {
  if (cached) return cached

  const out: ConfigSearchEntry[] = []
  const seen = new Set<string>()

  Object.values(menus).forEach((menu) =>
    push(out, seen, { id: `menu-${menu}`, menu, label: menu, keywords: menuKeywords[menu] }),
  )

  sources().forEach((source) => {
    let fields: FieldMap = {}
    try {
      fields = source.build() || {}
    } catch {
      return
    }
    walkFields(fields, source.menu, source.section, out, seen, source.section ? 1 : 0)
  })

  // Rules is a list of tabs, each with its own field map.
  try {
    getTabConfigs({}, {}).forEach((tab) => {
      push(out, seen, { id: `rules-tab-${tab.key}`, menu: menus.rules, section: 'Tab', label: tab.title })
      walkFields(tab.fields({}) || {}, menus.rules, tab.title, out, seen, 1)
    })
  } catch {
    /* a tab config that needs live status data is simply not indexed */
  }

  // Quick Links: every page the menu can point at.
  try {
    Object.keys(featureSubMenus).forEach((key) => {
      const title = (featureSubMenus as Record<string, any>)[key]?.title
      if (title)
        push(out, seen, {
          id: `feature-sub-${key}`,
          menu: menus.features,
          section: 'Submenu',
          label: title,
          keywords: key,
        })
    })
  } catch {
    /* ignore */
  }

  // Document Generate limits are described by a category list, not a field map.
  try {
    limitCategories.forEach((category) => {
      push(out, seen, {
        id: `docgen-limit-${category.key}`,
        menu: menus.hud1,
        section: 'Document Generate',
        label: category.title,
        keywords: `${category.fieldTitle} ${category.minTitle} ${category.maxTitle}`,
      })
    })
  } catch {
    /* ignore */
  }

  staticEntries.forEach((entry, idx) =>
    push(out, seen, {
      id: `static-${idx}`,
      menu: entry.menu,
      section: entry.section,
      label: entry.label,
      keywords: entry.keywords,
    }),
  )

  cached = out
  return out
}

/**
 * Servicing Information fields are defined by the backend, so they are pulled
 * once (lazily) rather than read off a constant.
 */
export const loadServicingInfoEntries = async (): Promise<ConfigSearchEntry[]> => {
  try {
    const res: any = await getAdminConfig('servicingInfoConfig')
    const out: ConfigSearchEntry[] = []
    const seen = new Set<string>()
    walkFields(res?.inputs || {}, menus.servicingInformation, undefined, out, seen, 0)
    return out
  } catch {
    return []
  }
}
